import { drivelabels_v2 } from '@googleapis/drivelabels';
import { Messages } from '../../../utils/message-constants';
import { DriveRoles } from '../../../utils/roles/client-roles';
import { getLabelLifecycleStatus } from './label-lifecycle';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

export type LabelMenuActionKey =
  | 'edit'
  | 'clone'
  | 'publish'
  | 'enable'
  | 'disable'
  | 'delete';

export interface LabelMenuAction {
  key: LabelMenuActionKey;
  label: string;
  hint?: string;
  tooltip?: string;
  disabled: boolean;
}

export interface LabelMenuContext {
  hasActiveLock: boolean;
  canPublish: boolean;
  isPublishing?: boolean;
  canClone: boolean;
  canEnable: boolean;
  canDisable: boolean;
  canDelete: boolean;
  /** Offers Edit first when set; left out, the menu has no Edit item. */
  canEdit?: boolean;
  /** A delete is running, so Delete waits until it finishes. */
  isDeleting?: boolean;
  hasWriteAccess: boolean | undefined;
}

export function getLabelMenuActions(
  label: Label,
  context: LabelMenuContext
): LabelMenuAction[] {
  const {
    hasActiveLock,
    canPublish,
    isPublishing,
    canClone,
    canEnable,
    canDisable,
    canDelete,
    canEdit,
    isDeleting,
    hasWriteAccess,
  } = context;
  const missingScope = hasWriteAccess === false;

  const build = (
    key: LabelMenuActionKey,
    label: string,
    allowed: boolean,
    roleName: string,
    statusHint?: string
  ): LabelMenuAction => {
    const tooltip = !allowed
      ? Messages.Errors.Permission.Acting(roleName)
      : missingScope
      ? Messages.DriveLabels.NoWriteAccess
      : undefined;
    const hint = tooltip ? undefined : statusHint;
    return {
      key,
      label,
      hint,
      tooltip,
      disabled: !!tooltip || !!hint || hasWriteAccess === undefined,
    };
  };

  const publish = () =>
    build(
      'publish',
      'Publish',
      canPublish,
      DriveRoles.CreateDriveLabels.name,
      isPublishing ? Messages.DriveLabels.PublishInProgress : undefined
    );
  // Clone works for a label in any status, locked or not.
  const clone = () =>
    build('clone', 'Clone', canClone, DriveRoles.CreateDriveLabels.name);

  const enable = () =>
    build(
      'enable',
      'Enable',
      canEnable,
      DriveRoles.UpdateDriveLabelsStatus.name
    );

  const disable = (statusHint?: string) =>
    build(
      'disable',
      'Disable',
      canDisable,
      DriveRoles.UpdateDriveLabelsStatus.name,
      statusHint
    );

  const remove = (statusHint?: string) =>
    build(
      'delete',
      'Delete',
      canDelete,
      DriveRoles.DeleteDriveLabels.name,
      statusHint ??
        (isDeleting ? Messages.DriveLabels.DeleteInProgress : undefined)
    );

  const statusActions = (): LabelMenuAction[] => {
    switch (getLabelLifecycleStatus(label)) {
      case 'DRAFT':
        return [publish(), clone(), remove()];
      case 'DISABLED':
        return [clone(), enable(), remove()];
      case 'PUBLISHED':
        return hasActiveLock
          ? [clone(), disable(Messages.DriveLabels.DisableLockedHint)]
          : [
              clone(),
              disable(),
              remove(Messages.DriveLabels.DeletePublishedHint),
            ];
      case 'PUBLISHED_NEW_DRAFT':
        // A lock only blocks destructive edits, which can't be in the draft, so it still publishes.
        return hasActiveLock
          ? [
              publish(),
              clone(),
              disable(Messages.DriveLabels.DisableLockedHint),
            ]
          : [
              publish(),
              clone(),
              disable(),
              remove(Messages.DriveLabels.DeletePublishedHint),
            ];
      default:
        return [];
    }
  };

  const actions = statusActions();
  // Locked and disabled labels still open: Edit Label shows them read-only and says why.
  if (canEdit === undefined || actions.length === 0) {
    return actions;
  }
  return [
    build('edit', 'Edit', canEdit, DriveRoles.EditDriveLabels.name),
    ...actions,
  ];
}
